import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingCart, Search, X, ChevronLeft, SlidersHorizontal,
  Tag, Truck, Shield, Car, Heart, Zap,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import ShippingPage from "./ShippingPage";
import { API, useCart, useWishlist, PRODUCTS_PER_PAGE, SEARCH_DEBOUNCE_MS, MIN_SEARCH_LENGTH } from "./shop/shopData";
import ProductDetail from "./shop/ProductDetail";
import BrandCard from "./shop/BrandCard";
import CartSidebar from "./shop/CartSidebar";
import WishlistSidebar from "./shop/WishlistSidebar";
import ProductGrid from "./shop/ProductGrid";
import CarFilter from "./shop/CarFilter";
import EngineCodeFilter from "./shop/EngineCodeFilter";
import VehicleSelector from "./shop/VehicleSelector";
import BrandsForCarBar from "./shop/BrandsForCarBar";
import { formatCarModel } from "./shop/formatCarModel";

// Motorcode detectie: alphanumeric 2-10 chars (met optioneel streepje) zonder spaties
const ENGINE_CODE_REGEX = /^[A-Z0-9][A-Z0-9-]{1,9}$/i;
const isLikelyEngineCode = (val) => {
  if (!val) return false;
  const trimmed = val.trim();
  if (trimmed.length < 3 || trimmed.length > 10) return false;
  if (!ENGINE_CODE_REGEX.test(trimmed)) return false;
  // Moet minstens 1 letter EN 1 cijfer bevatten, OF minstens 3 hoofdletters (bv. BWA, TFSI)
  const hasDigit = /\d/.test(trimmed);
  const hasLetter = /[A-Za-z]/.test(trimmed);
  const upperCount = (trimmed.match(/[A-Z]/g) || []).length;
  return (hasDigit && hasLetter) || upperCount >= 3;
};

export default function Shop({ onBack, initialProduct, initialCarMake, onContactClick }) {
  const cart = useCart();
  const wishlist = useWishlist();
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [viewMode, setViewMode] = useState("brands");
  const [activeBrand, setActiveBrand] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [activeProduct, setActiveProduct] = useState(initialProduct || null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [brands, setBrands] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [searchMode, setSearchMode] = useState(false);
  const searchTimeout = useRef(null);
  const [carMakes, setCarMakes] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [selectedCarMake, setSelectedCarMake] = useState(initialCarMake || null);
  const [selectedCarModel, setSelectedCarModel] = useState(null);
  const [carSearchQuery, setCarSearchQuery] = useState("");
  const [productCategories, setProductCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categoryBrands, setCategoryBrands] = useState([]);
  const [activeCategoryBrand, setActiveCategoryBrand] = useState(null);
  const [sortBy, setSortBy] = useState("name_asc");
  const [showShipping, setShowShipping] = useState(false);
  const [filterCarMake, setFilterCarMake] = useState(null);
  const [filterCarModel, setFilterCarModel] = useState(null);
  const [filterEngineCode, setFilterEngineCode] = useState(null);
  // Motorswap-modus: als aan, includeert chassis-onderdelen (remschijven, ophanging) van de auto
  // ook al staat er een motorcode-filter actief. Producten met engine_codes moeten dan wel matchen.
  const [engineSwap, setEngineSwap] = useState(false);
  const [savedCar, setSavedCar] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ttw_saved_car") || "null"); } catch { return null; }
  });
  // RDW-details van de laatst-opgezochte kenteken lookup (bouwjaar, brandstof, exact model, motorcode suggesties)
  const [rdwInfo, setRdwInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ttw_rdw_info") || "null"); } catch { return null; }
  });
  const [engineCodeSuggestions, setEngineCodeSuggestions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ttw_engine_suggestions") || "[]"); } catch { return []; }
  });

  const saveCar = (make, model, engineCode) => {
    if (make) {
      const car = { make, model: model || null, engineCode: engineCode || null };
      localStorage.setItem("ttw_saved_car", JSON.stringify(car));
      setSavedCar(car);
    } else {
      localStorage.removeItem("ttw_saved_car");
      localStorage.removeItem("ttw_rdw_info");
      localStorage.removeItem("ttw_engine_suggestions");
      setSavedCar(null);
      setRdwInfo(null);
      setEngineCodeSuggestions([]);
    }
  };

  const clearSavedCar = () => {
    localStorage.removeItem("ttw_saved_car");
    setSavedCar(null);
    setSelectedCarMake(null);
    setSelectedCarModel(null);
    setFilterCarMake(null);
    setFilterCarModel(null);
    setFilterEngineCode(null);
  };

  useEffect(() => {
    axios.get(`${API}/products/brands`).then((r) => setBrands(r.data)).catch(() => {});
    axios.get(`${API}/products/car-makes`).then((r) => setCarMakes(r.data)).catch(() => {});
    axios.get(`${API}/products/categories`).then((r) => setProductCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (initialCarMake && !initialProduct) { setViewMode("cars"); setSelectedCarMake(initialCarMake); }
  }, [initialCarMake, initialProduct]);

  // Bewaarde auto: bij shop-opening (geen brand/cat/search actief én geen
  // initiële selectie via props) laden we direct de producten voor de auto
  // die de klant vorige keer heeft gekozen.
  const [autoLoadedSaved, setAutoLoadedSaved] = useState(false);
  useEffect(() => {
    if (autoLoadedSaved) return;
    if (initialCarMake || initialProduct) { setAutoLoadedSaved(true); return; }
    if (savedCar?.make) {
      setViewMode("cars");
      setSelectedCarMake(savedCar.make);
      if (savedCar.model) setSelectedCarModel(savedCar.model);
      if (savedCar.engineCode) setFilterEngineCode(savedCar.engineCode);
    }
    setAutoLoadedSaved(true);
  }, []);

  // Fetch products
  useEffect(() => {
    if (!activeBrand && !searchMode && !selectedCarMake && !activeCategory && !filterEngineCode) return;
    setLoading(true);
    const params = { page, limit: PRODUCTS_PER_PAGE };
    if (activeBrand) params.brand = activeBrand;
    if (activeSubcategory) params.subcategory = activeSubcategory;
    if (selectedCarMake) params.car_make = selectedCarMake;
    if (selectedCarModel) params.car_model = selectedCarModel;
    if (activeCategory) params.product_category = activeCategory;
    if (activeCategoryBrand) params.brand = activeCategoryBrand;
    if (filterCarMake && (activeBrand || activeCategory)) params.car_make = filterCarMake;
    if (filterCarModel && (activeBrand || activeCategory)) params.car_model = filterCarModel;
    // Motorcode filter altijd meesturen zodra het is gezet (ook zonder brand/categorie/auto → pure motorcode-search)
    if (filterEngineCode) params.engine_code = filterEngineCode;
    // Motorswap-modus: als motorcode + car_make actief zijn, laat ook chassis-onderdelen door
    if (filterEngineCode && engineSwap && (selectedCarMake || filterCarMake)) params.engine_swap = true;
    if (sortBy && sortBy !== "name_asc") params.sort = sortBy;
    if (searchMode && globalSearch.length >= MIN_SEARCH_LENGTH) params.search = globalSearch;
    else if (searchQuery.length >= MIN_SEARCH_LENGTH) params.search = searchQuery;
    else if (carSearchQuery.length >= MIN_SEARCH_LENGTH) params.search = carSearchQuery;
    axios.get(`${API}/products`, { params })
      .then((r) => { setProducts(r.data.items); setTotalPages(r.data.pages); setTotalProducts(r.data.total); })
      .catch(() => toast.error("Producten konden niet worden geladen"))
      .finally(() => setLoading(false));
  }, [activeBrand, activeSubcategory, page, searchQuery, searchMode, globalSearch, selectedCarMake, selectedCarModel, carSearchQuery, activeCategory, activeCategoryBrand, sortBy, filterCarMake, filterCarModel, filterEngineCode, engineSwap]);

  useEffect(() => {
    if (!activeBrand) { setSubcategories([]); return; }
    axios.get(`${API}/products/subcategories`, { params: { brand: activeBrand } }).then((r) => setSubcategories(r.data)).catch(() => {});
  }, [activeBrand]);

  useEffect(() => {
    if (!selectedCarMake) { setCarModels([]); return; }
    axios.get(`${API}/products/car-models`, { params: { make: selectedCarMake } }).then((r) => setCarModels(r.data)).catch(() => {});
  }, [selectedCarMake]);

  useEffect(() => {
    if (!activeCategory) { setCategoryBrands([]); return; }
    axios.get(`${API}/products/category-brands`, { params: { product_category: activeCategory } }).then((r) => setCategoryBrands(r.data)).catch(() => {});
  }, [activeCategory]);

  const selectBrand = (brand) => { setActiveBrand(brand); setPage(1); setSearchQuery(""); setActiveSubcategory(null); setSearchMode(false); setGlobalSearch(""); setSelectedCarMake(null); setSelectedCarModel(null); setActiveCategory(null); setActiveCategoryBrand(null); setFilterCarMake(savedCar?.make || null); setFilterCarModel(savedCar?.model || null); setFilterEngineCode(null); };
  const selectCategory = (cat) => { setActiveCategory(cat); setActiveCategoryBrand(null); setPage(1); setSearchQuery(""); setActiveBrand(null); setSelectedCarMake(null); setSearchMode(false); setFilterCarMake(savedCar?.make || null); setFilterCarModel(savedCar?.model || null); setFilterEngineCode(null); };
  const selectCarMake = (make) => { setSelectedCarMake(make); setSelectedCarModel(null); setPage(1); setCarSearchQuery(""); setActiveBrand(null); setSearchMode(false); saveCar(make, null, null); };
  const selectCarModel = (model) => { setSelectedCarModel(model); setPage(1); setCarSearchQuery(""); saveCar(selectedCarMake, model, filterEngineCode); };
  const handleGlobalSearch = (val) => {
    setGlobalSearch(val);
    clearTimeout(searchTimeout.current);
    if (val.length >= MIN_SEARCH_LENGTH) { searchTimeout.current = setTimeout(() => { setSearchMode(true); setActiveBrand(null); setActiveSubcategory(null); setSelectedCarMake(null); setSelectedCarModel(null); setPage(1); }, SEARCH_DEBOUNCE_MS); }
    else if (val.length === 0) setSearchMode(false);
  };
  const handleBrandSearch = (val) => { setSearchQuery(val); setPage(1); };
  const resetToHome = () => { setActiveBrand(null); setSelectedCarMake(null); setSelectedCarModel(null); setActiveCategory(null); setActiveCategoryBrand(null); setSearchMode(false); setGlobalSearch(""); setProducts([]); setSearchQuery(""); setActiveSubcategory(null); setCarSearchQuery(""); setFilterCarMake(savedCar?.make || null); setFilterCarModel(savedCar?.model || null); setFilterEngineCode(null); };

  // Universal Vehicle Selector — pas een gekozen auto (merk/model/motorcode) toe.
  // Scope alle views (Per Merk / Per Categorie / Per Auto) automatisch naar deze auto.
  // rdw + engineCodeSuggestions komen mee als de auto uit de RDW kenteken-lookup komt.
  // engineSwap: als true, activeer motorswap-modus zodat chassis-onderdelen (remmen, ophanging)
  //   van het merk + motoronderdelen van het gekozen blok samen zichtbaar zijn.
  const applyVehicle = ({ make, model, engineCode, engineSwap: newSwap = false, rdw = null, engineCodeSuggestions: newSuggestions = null }) => {
    if (!make) return;
    // Als er al een merk/categorie actief is → filter binnen die context
    if (activeBrand || activeCategory) {
      setFilterCarMake(make);
      setFilterCarModel(model || null);
      setFilterEngineCode(engineCode || null);
    } else {
      // Anders → open de Per Auto view
      setViewMode("cars");
      setSelectedCarMake(make);
      setSelectedCarModel(model || null);
      setFilterEngineCode(engineCode || null);
      setActiveBrand(null);
      setActiveCategory(null);
    }
    setEngineSwap(Boolean(newSwap));
    setSearchMode(false);
    setGlobalSearch("");
    setPage(1);
    saveCar(make, model, engineCode);
    // RDW-info opslaan als aanwezig (voor exacte auto-weergave in sticky bar)
    if (rdw) {
      setRdwInfo(rdw);
      localStorage.setItem("ttw_rdw_info", JSON.stringify(rdw));
    } else {
      // Bij handmatige selectie: RDW-context wissen zodat we geen verouderde info tonen
      setRdwInfo(null);
      localStorage.removeItem("ttw_rdw_info");
    }
    if (Array.isArray(newSuggestions)) {
      setEngineCodeSuggestions(newSuggestions);
      localStorage.setItem("ttw_engine_suggestions", JSON.stringify(newSuggestions));
    } else if (!rdw) {
      setEngineCodeSuggestions([]);
      localStorage.removeItem("ttw_engine_suggestions");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Klik op de "Zoek als motorcode" chip in de zoekbalk
  const searchAsEngineCode = () => {
    if (!globalSearch) return;
    const code = globalSearch.trim().toUpperCase();
    // Als klant een bewaarde auto heeft → toon motorcode-hits BINNEN die auto
    if (savedCar?.make) {
      setFilterEngineCode(code);
      setSelectedCarMake(savedCar.make);
      setSelectedCarModel(savedCar.model || null);
      setViewMode("cars");
      setActiveBrand(null);
      setActiveCategory(null);
      setSearchMode(false);
      setGlobalSearch("");
      saveCar(savedCar.make, savedCar.model, code);
    } else {
      // Puur motorcode-search (over alle merken/auto's)
      setFilterEngineCode(code);
      setFilterCarMake(null);
      setFilterCarModel(null);
      setSelectedCarMake(null);
      setSelectedCarModel(null);
      setActiveBrand(null);
      setActiveCategory(null);
      // searchMode aan → gebruik searchMode-view als container, maar met engine_code filter
      setSearchMode(true);
      setGlobalSearch(code);
    }
    setPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showEngineCodeChip = globalSearch && isLikelyEngineCode(globalSearch);
  const activeVehicle = useMemo(() => {
    // Bepaal de "actieve auto context" — voor de sticky selector
    if (selectedCarMake) {
      return { make: selectedCarMake, model: selectedCarModel, engineCode: filterEngineCode };
    }
    if ((activeBrand || activeCategory) && filterCarMake) {
      return { make: filterCarMake, model: filterCarModel, engineCode: filterEngineCode };
    }
    return null;
  }, [selectedCarMake, selectedCarModel, activeBrand, activeCategory, filterCarMake, filterCarModel, filterEngineCode]);

  const clearActiveVehicle = () => {
    setSelectedCarMake(null);
    setSelectedCarModel(null);
    setFilterCarMake(null);
    setFilterCarModel(null);
    setFilterEngineCode(null);
    clearSavedCar();
    setPage(1);
  };

  const totalBrandProducts = useMemo(() => brands.reduce((s, b) => s + b.count, 0), [brands]);
  const sortedCategories = useMemo(() => [...productCategories].sort((a, b) => a.category.localeCompare(b.category)), [productCategories]);
  const filteredCarMakes = useMemo(() => carMakes.filter(m => m.count >= 20), [carMakes]);
  const gridProps = { products, loading, onAdd: cart.addItem, onView: setActiveProduct, page, totalPages, onPageChange: setPage, sortBy, onSort: (v) => { setSortBy(v); setPage(1); }, total: totalProducts, onToggleWishlist: wishlist.toggle, isInWishlist: (slug) => wishlist.has(slug) };

  return (
    <section id="shop" data-testid="shop-section" className="min-h-screen bg-[#0A0A0A] pt-24 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <button data-testid="shop-back-btn" onClick={onBack} className="flex items-center gap-1 text-[#666] hover:text-white text-sm mb-3 transition-colors"><ChevronLeft size={16} /> Terug naar home</button>
            <div className="flex items-center gap-3">
              <img src="/ttw-logo.png" alt="TTW Engineering" className="h-8 w-auto" />
              <span className="text-[#FF3B30] text-2xl sm:text-3xl font-bold font-['Orbitron']">Shop</span>
            </div>
            <p className="text-[#666] mt-1.5 text-sm">{totalBrandProducts.toLocaleString("nl-NL")} performance onderdelen van topmerken</p>
            <p className="text-[#555] mt-1 text-[10px]">Alle prijzen incl. BTW · Prijzen onder voorbehoud van leverancierstarieven</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="wishlist-toggle-btn" onClick={() => setWishlistOpen(true)} className="relative bg-[#111] border border-[#1E1E1E] rounded-xl p-3.5 hover:border-[#FF3B30]/40 transition-all group" title="Verlanglijst">
              <Heart size={22} className={`transition-colors ${wishlist.count > 0 ? "text-[#FF3B30]" : "text-white group-hover:text-[#FF3B30]"}`} fill={wishlist.count > 0 ? "#FF3B30" : "none"} />
              {wishlist.count > 0 && <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] bg-[#FF3B30] rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1 shadow-[0_0_10px_rgba(255,59,48,0.4)]">{wishlist.count}</span>}
            </button>
            <button data-testid="cart-toggle-btn" onClick={() => setCartOpen(true)} className="relative bg-[#111] border border-[#1E1E1E] rounded-xl p-3.5 hover:border-[#FF3B30]/40 transition-all group">
              <ShoppingCart size={22} className="text-white group-hover:text-[#FF3B30] transition-colors" />
              {cart.count > 0 && <span className="absolute -top-2 -right-2 min-w-[22px] h-[22px] bg-[#FF3B30] rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1 shadow-[0_0_10px_rgba(255,59,48,0.4)]">{cart.count}</span>}
            </button>
          </div>
        </div>

        {showShipping ? (
          <ShippingPage onBack={() => setShowShipping(false)} />
        ) : activeProduct ? (
          <ProductDetail
            product={activeProduct}
            onBack={() => setActiveProduct(null)}
            onAdd={cart.addItem}
            onToggleWishlist={wishlist.toggle}
            inWishlist={wishlist.has(activeProduct.slug)}
            onEngineCodeClick={(code) => {
              setActiveProduct(null);
              setActiveBrand(null);
              setActiveCategory(null);
              setSelectedCarMake(null);
              setSelectedCarModel(null);
              setSearchMode(true);
              setGlobalSearch(code);
              setPage(1);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onMakeClick={(make) => {
              setActiveProduct(null);
              setActiveBrand(null);
              setActiveCategory(null);
              setSelectedCarMake(make);
              setSelectedCarModel(null);
              setSearchMode(false);
              setGlobalSearch("");
              setPage(1);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        ) : (
        <>
          {/* Sticky vehicle context bar — altijd zichtbaar zodra er een auto is gekozen */}
          {activeVehicle && (
            <VehicleSelector
              variant="sticky"
              make={activeVehicle.make}
              model={activeVehicle.model}
              engineCode={activeVehicle.engineCode}
              rdwInfo={rdwInfo}
              engineCodeSuggestions={engineCodeSuggestions}
              engineSwap={engineSwap}
              onToggleEngineSwap={() => setEngineSwap((v) => !v)}
              onClear={clearActiveVehicle}
              onClearEngineCode={() => {
                setFilterEngineCode(null);
                setEngineSwap(false);
                setPage(1);
                if (savedCar?.make) saveCar(savedCar.make, savedCar.model, null);
              }}
              onSelectEngineCode={(code) => {
                setFilterEngineCode(code);
                // Default: toon chassis + motorcode-onderdelen (het meest bruikbaar).
                // Klant kan handmatig "alleen motor" strict-modus aanzetten.
                setEngineSwap(true);
                setPage(1);
                saveCar(savedCar?.make || activeVehicle.make, savedCar?.model, code);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          )}

          {/* Global Search */}
          <div className="relative mb-2">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#555]" />
            <input data-testid="global-search" value={globalSearch} onChange={(e) => handleGlobalSearch(e.target.value)}
              placeholder="Zoek door alle producten... bijv. 'Golf', 'Civic', 'turbo', 'BWA'"
              className="w-full pl-11 pr-4 py-3.5 bg-[#111] border border-[#1E1E1E] rounded-xl text-white text-sm placeholder:text-[#444] focus:border-[#FF3B30]/50 focus:outline-none transition-colors" />
            {(searchMode || globalSearch) && <button onClick={() => { setGlobalSearch(""); setSearchMode(false); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#555] hover:text-white transition-colors"><X size={16} /></button>}
          </div>

          {/* Smart motorcode-detectie chip */}
          {showEngineCodeChip && (
            <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="engine-code-detection">
              <span className="text-[10px] uppercase tracking-widest text-[#888]">Lijkt op een motorcode:</span>
              <button
                onClick={searchAsEngineCode}
                data-testid="search-as-engine-code"
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F1C40F]/10 border border-[#F1C40F]/40 rounded-full text-[#F1C40F] text-xs font-bold hover:bg-[#F1C40F]/20 transition-all"
              >
                <Zap size={11} /> Zoek &ldquo;{globalSearch.toUpperCase()}&rdquo; als motorcode
              </button>
            </div>
          )}

          {/* Hero Vehicle Selector — alleen tonen op home view (geen brand/cat/search/car actief) */}
          {!activeBrand && !searchMode && !selectedCarMake && !activeCategory && !activeVehicle && (
            <VehicleSelector
              variant="hero"
              make={savedCar?.make || ""}
              model={savedCar?.model || ""}
              engineCode={savedCar?.engineCode || ""}
              onApply={applyVehicle}
              onClear={clearSavedCar}
            />
          )}

          {/* Bewaarde auto banner — toont altijd de auto van vorige bezoek met wissel/verwijder knoppen */}
          {savedCar?.make && !activeVehicle && (activeBrand || activeCategory) && (
            <div
              data-testid="saved-car-banner"
              className="mb-5 bg-gradient-to-r from-[#0f1a0a] via-[#111] to-[#0f1a0a] border border-[#27AE60]/25 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3"
            >
              <div className="w-9 h-9 rounded-lg bg-[#27AE60]/15 border border-[#27AE60]/30 flex items-center justify-center flex-shrink-0">
                <Car size={17} className="text-[#27AE60]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#27AE60] text-[10px] uppercase tracking-widest font-bold">Jouw auto (onthouden)</p>
                <p className="text-white text-sm font-semibold truncate">
                  {savedCar.make}
                  {savedCar.model && <span className="text-[#ccc]"> · {savedCar.model}</span>}
                  {savedCar.engineCode && <span className="text-[#F1C40F] ml-2 font-mono text-xs">{savedCar.engineCode}</span>}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedCarMake(savedCar.make);
                  setSelectedCarModel(savedCar.model || null);
                  setFilterEngineCode(savedCar.engineCode || null);
                  setViewMode("cars");
                  setActiveBrand(null);
                  setActiveCategory(null);
                  setSearchMode(false);
                  setGlobalSearch("");
                  setPage(1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#FF3B30] text-white hover:bg-[#E0342A] transition-colors"
                data-testid="show-my-parts"
              >
                Toon mijn onderdelen
              </button>
              <button
                onClick={clearSavedCar}
                className="text-[#666] hover:text-[#E74C3C] text-xs px-2 py-1 transition-colors"
                data-testid="clear-saved-car"
                title="Wissel auto / vergeet"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* OEM & Wossner Banner */}
          {!activeBrand && !searchMode && !selectedCarMake && !activeCategory && (
            <div data-testid="shop-oem-banner" onClick={() => { if (onContactClick) onContactClick(); }}
              className="mb-6 bg-gradient-to-r from-[#1a0808] via-[#111] to-[#0a1a2a] border border-[#FF3B30]/20 rounded-xl p-4 cursor-pointer hover:border-[#FF3B30]/50 transition-all group">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <img src="/logos/wossner.png" alt="Wössner" className="h-7 w-auto" />
                    <div className="h-7 px-2.5 bg-[#3498DB] rounded flex items-center justify-center">
                      <span className="text-white text-xs font-black tracking-wider" style={{ fontFamily: "'Orbitron', sans-serif" }}>OEM</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">Wossner Forged Pistons &amp; OEM Onderdelen</p>
                    <p className="text-[#888] text-xs">Gesmede zuigers, drijfstangen &amp; originele onderdelen — vraag aan via contact</p>
                  </div>
                </div>
                <span className="text-[#FF3B30] text-xs font-semibold whitespace-nowrap group-hover:underline">Offerte aanvragen →</span>
              </div>
            </div>
          )}

          {/* Trust Bar */}
          {!activeBrand && !searchMode && !selectedCarMake && (
            <div data-testid="trust-bar" className="grid grid-cols-3 gap-3 mb-8">
              {[
                { icon: Tag, text: "Scherpe prijzen", sub: "Direct van de leverancier" },
                { icon: Truck, text: "Snelle levering", sub: "Verzending door heel NL", link: true },
                { icon: Shield, text: "Betrouwbaar", sub: "Professioneel advies" },
              ].map((item) => (
                <div key={item.text} className={`flex items-center gap-3 bg-[#111] border border-[#1E1E1E] rounded-xl px-4 py-3 ${item.link ? 'cursor-pointer hover:border-[#FF3B30]/40 transition-all' : ''}`}
                  onClick={item.link ? () => setShowShipping(true) : undefined}>
                  <item.icon size={18} className="text-[#FF3B30] flex-shrink-0" />
                  <div><p className="text-white text-xs font-semibold">{item.text}</p><p className="text-[#555] text-[10px]">{item.sub}</p></div>
                </div>
              ))}
            </div>
          )}

          {/* View Mode Toggle */}
          {!activeBrand && !searchMode && !selectedCarMake && !activeCategory && (
            <div className="flex gap-2 mb-6" data-testid="view-mode-toggle">
              {[
                { mode: "brands", icon: Tag, label: "Per Merk" },
                { mode: "categories", icon: SlidersHorizontal, label: "Per Categorie" },
                { mode: "cars", icon: Car, label: "Per Auto" },
              ].map((v) => (
                <button key={v.mode} data-testid={`view-mode-${v.mode}`}
                  onClick={() => { setViewMode(v.mode); resetToHome(); }}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all border ${viewMode === v.mode ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#111] text-[#666] border-[#1E1E1E] hover:text-white hover:border-[#FF3B30]/30"}`}>
                  <v.icon size={14} className="inline mr-2 -mt-0.5" />{v.label}
                </button>
              ))}
            </div>
          )}

          {/* Content Views */}
          {searchMode ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={() => { setSearchMode(false); setGlobalSearch(""); setFilterEngineCode(null); }} className="flex items-center gap-1 text-[#666] hover:text-white text-sm transition-colors"><ChevronLeft size={16} /> Terug</button>
                  <span className="text-[#1E1E1E]">|</span>
                  {filterEngineCode ? (
                    <span className="text-white font-semibold flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F1C40F]/10 border border-[#F1C40F]/30 rounded text-[#F1C40F] font-mono text-xs">
                        <Zap size={11} /> Motorcode: {filterEngineCode}
                      </span>
                    </span>
                  ) : (
                    <span className="text-white font-semibold">Zoekresultaten voor &ldquo;{globalSearch}&rdquo;</span>
                  )}
                  <span className="text-[#666] text-sm">({totalProducts} resultaten)</span>
                </div>
              </div>
              <ProductGrid {...gridProps} />
            </>
          ) : activeCategory ? (
            <>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3 flex-wrap">
                  <button data-testid="back-to-categories" onClick={resetToHome} className="flex items-center gap-1 text-[#666] hover:text-white text-sm transition-colors"><ChevronLeft size={16} /> Alle categorieën</button>
                  <span className="text-[#1E1E1E]">/</span><span className="text-white font-bold text-lg">{activeCategory}</span>
                  {activeCategoryBrand && <><span className="text-[#1E1E1E]">/</span><span className="text-[#FF3B30] font-semibold">{activeCategoryBrand}</span></>}
                  <span className="text-[#555] text-sm">({totalProducts.toLocaleString("nl-NL")} producten)</span>
                </div>
                <div className="relative w-full sm:w-72">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                  <Input data-testid="category-search" value={searchQuery} onChange={(e) => handleBrandSearch(e.target.value)} placeholder={`Zoek in ${activeCategory}...`} className="pl-9 bg-[#111] border-[#1E1E1E] text-white text-sm h-10 rounded-lg" />
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1.5 mb-3 scrollbar-hide">
                {productCategories.map((c) => (
                  <button key={c.category} onClick={() => selectCategory(c.category)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${activeCategory === c.category ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#111] text-[#666] border-[#1E1E1E] hover:text-white hover:border-[#FF3B30]/30"}`}>{c.category}</button>
                ))}
              </div>
              {categoryBrands.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1.5 mb-6 scrollbar-hide">
                  <button onClick={() => { setActiveCategoryBrand(null); setPage(1); setFilterCarMake(savedCar?.make || null); setFilterCarModel(savedCar?.model || null); setFilterEngineCode(null); }} data-testid="cat-brand-all"
                    className={`px-3 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all border flex items-center gap-1.5 ${!activeCategoryBrand ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#0D0D0D] text-[#666] border-[#1E1E1E] hover:text-white"}`}>
                    <SlidersHorizontal size={11} /> Alle merken
                  </button>
                  {categoryBrands.map((b) => (
                    <button key={b.brand} onClick={() => { setActiveCategoryBrand(b.brand); setPage(1); setFilterCarMake(savedCar?.make || null); setFilterCarModel(savedCar?.model || null); setFilterEngineCode(null); }}
                      className={`px-3 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all border ${activeCategoryBrand === b.brand ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#0D0D0D] text-[#666] border-[#1E1E1E] hover:text-white"}`}>{b.brand} ({b.count})</button>
                  ))}
                </div>
              )}
              <CarFilter
                productCategory={activeCategory}
                brand={activeCategoryBrand}
                activeCarMake={filterCarMake}
                activeCarModel={filterCarModel}
                onFilter={(make, model) => { setFilterCarMake(make); setFilterCarModel(model); setPage(1); saveCar(make, model); }}
              />
              <EngineCodeFilter
                productCategory={activeCategory}
                brand={activeCategoryBrand}
                activeEngineCode={filterEngineCode}
                onFilter={(code) => { setFilterEngineCode(code); setPage(1); if (savedCar?.make) saveCar(savedCar.make, savedCar.model, code); }}
              />
              <ProductGrid {...gridProps} />
            </>
          ) : selectedCarMake ? (
            <>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3 flex-wrap">
                  <button data-testid="back-to-cars" onClick={resetToHome} className="flex items-center gap-1 text-[#666] hover:text-white text-sm transition-colors"><ChevronLeft size={16} /> Alle auto&apos;s</button>
                  <span className="text-[#1E1E1E]">/</span><span className="text-white font-bold text-lg">{selectedCarMake}</span>
                  {selectedCarModel && <><span className="text-[#1E1E1E]">/</span><span className="text-[#FF3B30] font-semibold" title={selectedCarModel}>{formatCarModel(selectedCarModel, selectedCarMake).display}</span></>}
                  <span className="text-[#555] text-sm">({totalProducts.toLocaleString("nl-NL")} producten)</span>
                </div>
                <div className="relative w-full sm:w-72">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                  <Input data-testid="car-search" value={carSearchQuery} onChange={(e) => { setCarSearchQuery(e.target.value); setPage(1); }} placeholder={`Zoek in ${selectedCarMake}...`} className="pl-9 bg-[#111] border-[#1E1E1E] text-white text-sm h-10 rounded-lg" />
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1.5 mb-3 scrollbar-hide">
                {filteredCarMakes.map((m) => (
                  <button key={m.make} data-testid={`car-make-pill-${m.make.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => selectCarMake(m.make)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${selectedCarMake === m.make ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#111] text-[#666] border-[#1E1E1E] hover:text-white hover:border-[#FF3B30]/30"}`}>{m.make}</button>
                ))}
              </div>
              {carModels.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1.5 mb-4 scrollbar-hide">
                  <button onClick={() => { setSelectedCarModel(null); setPage(1); }} data-testid="car-model-all"
                    className={`px-3 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all border flex items-center gap-1.5 ${!selectedCarModel ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#0D0D0D] text-[#666] border-[#1E1E1E] hover:text-white"}`}>
                    <SlidersHorizontal size={11} /> Alle modellen
                  </button>
                  {carModels.slice(0, 30).map((m) => {
                    const { display, details } = formatCarModel(m.model, selectedCarMake);
                    return (
                      <button key={m.model} data-testid="car-model-pill" onClick={() => selectCarModel(m.model)}
                        title={details ? `${m.model} · ${details}` : m.model}
                        className={`px-3 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all border ${selectedCarModel === m.model ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#0D0D0D] text-[#666] border-[#1E1E1E] hover:text-white"}`}>
                        {display} ({m.count})
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Onderdeel-merken beschikbaar voor deze auto (user-request) */}
              <BrandsForCarBar
                carMake={selectedCarMake}
                carModel={selectedCarModel}
                engineCode={filterEngineCode}
                activeBrand={activeCategoryBrand}
                onSelectBrand={(brand) => {
                  if (brand) {
                    // Ga naar merk-view met de auto-filter behouden
                    setActiveBrand(brand);
                    setFilterCarMake(selectedCarMake);
                    setFilterCarModel(selectedCarModel);
                    setSelectedCarMake(null);
                    setSelectedCarModel(null);
                    setPage(1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    setActiveCategoryBrand(null);
                  }
                }}
              />
              {/* Motorcode filter binnen de auto — hergebruik EngineCodeFilter */}
              <EngineCodeFilter
                carMake={selectedCarMake}
                activeEngineCode={filterEngineCode}
                onFilter={(code) => { setFilterEngineCode(code); setPage(1); if (savedCar?.make) saveCar(savedCar.make, savedCar.model, code); }}
              />
              <ProductGrid {...gridProps} />
            </>
          ) : viewMode === "categories" && !activeBrand ? (
            <div data-testid="category-grid">
              <div className="flex items-center justify-between mb-5"><h2 className="text-white font-bold text-lg">Zoek op producttype</h2><span className="text-[#555] text-xs">{productCategories.length} categorieën</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {sortedCategories.map((c) => (
                  <button key={c.category} data-testid={`category-${c.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} onClick={() => selectCategory(c.category)}
                    className="group bg-[#111] border border-[#1E1E1E] rounded-xl px-5 py-4 text-left hover:border-[#FF3B30]/40 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,59,48,0.06)]">
                    <h3 className="text-white font-bold text-sm group-hover:text-[#FF3B30] transition-colors">{c.category}</h3>
                    <p className="text-[#555] text-[11px] mt-1">{c.count.toLocaleString("nl-NL")} producten</p>
                  </button>
                ))}
              </div>
            </div>
          ) : viewMode === "cars" && !activeBrand ? (
            <div data-testid="car-grid">
              <div className="flex items-center justify-between mb-5"><h2 className="text-white font-bold text-lg">Kies jouw auto</h2><span className="text-[#555] text-xs">{carMakes.length} automerken</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {carMakes.map((m) => (
                  <button key={m.make} data-testid={`car-make-${m.make.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => selectCarMake(m.make)}
                    className="group bg-[#111] border border-[#1E1E1E] rounded-xl px-4 py-4 text-left hover:border-[#FF3B30]/40 transition-all duration-300">
                    <h3 className="text-white font-bold text-sm group-hover:text-[#FF3B30] transition-colors">{m.make}</h3>
                    <p className="text-[#555] text-[11px] mt-1">{m.count} producten</p>
                  </button>
                ))}
              </div>
            </div>
          ) : !activeBrand ? (
            <div data-testid="brand-grid">
              <div className="flex items-center justify-between mb-5"><h2 className="text-white font-bold text-lg">Kies een merk</h2><span className="text-[#555] text-xs">{brands.length} merken</span></div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {brands.map((b) => <BrandCard key={b.brand} brand={b.brand} count={b.count} onClick={() => selectBrand(b.brand)} />)}
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3 flex-wrap">
                  <button data-testid="back-to-brands" onClick={resetToHome} className="flex items-center gap-1 text-[#666] hover:text-white text-sm transition-colors"><ChevronLeft size={16} /> Alle merken</button>
                  <span className="text-[#1E1E1E]">/</span><span className="text-white font-bold text-lg">{activeBrand}</span>
                  <span className="text-[#555] text-sm">({totalProducts.toLocaleString("nl-NL")} producten)</span>
                </div>
                <div className="relative w-full sm:w-72">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
                  <Input data-testid="shop-search" value={searchQuery} onChange={(e) => handleBrandSearch(e.target.value)} placeholder={`Zoek in ${activeBrand}...`} className="pl-9 bg-[#111] border-[#1E1E1E] text-white text-sm h-10 rounded-lg" />
                </div>
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-hide">
                  {brands.map((b) => (
                    <button key={b.brand} data-testid={`brand-pill-${b.brand.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => selectBrand(b.brand)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${activeBrand === b.brand ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#111] text-[#666] border-[#1E1E1E] hover:text-white hover:border-[#FF3B30]/30"}`}>{b.brand}</button>
                  ))}
                </div>
                {subcategories.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-hide">
                    <button onClick={() => { setActiveSubcategory(null); setPage(1); }} data-testid="subcat-all"
                      className={`px-3 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all border flex items-center gap-1.5 ${!activeSubcategory ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#0D0D0D] text-[#666] border-[#1E1E1E] hover:text-white"}`}>
                      <SlidersHorizontal size={11} /> Alles
                    </button>
                    {subcategories.map((s) => (
                      <button key={s.subcategory} data-testid={`subcat-${s.subcategory.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => { setActiveSubcategory(s.subcategory); setPage(1); }}
                        className={`px-3 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all border ${activeSubcategory === s.subcategory ? "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40" : "bg-[#0D0D0D] text-[#666] border-[#1E1E1E] hover:text-white"}`}>{s.subcategory} ({s.count})</button>
                    ))}
                  </div>
                )}
              </div>
              <CarFilter
                brand={activeBrand}
                activeCarMake={filterCarMake}
                activeCarModel={filterCarModel}
                onFilter={(make, model) => { setFilterCarMake(make); setFilterCarModel(model); setPage(1); saveCar(make, model); }}
              />
              <EngineCodeFilter
                brand={activeBrand}
                activeEngineCode={filterEngineCode}
                onFilter={(code) => { setFilterEngineCode(code); setPage(1); if (savedCar?.make) saveCar(savedCar.make, savedCar.model, code); }}
              />
              <ProductGrid {...gridProps} />
            </>
          )}
        </>
        )}
      </div>
      <CartSidebar cart={cart} isOpen={cartOpen} onClose={() => setCartOpen(false)} />
      <WishlistSidebar
        wishlist={wishlist}
        isOpen={wishlistOpen}
        onClose={() => setWishlistOpen(false)}
        onAddToCart={cart.addItem}
        onOpenProduct={(item) => {
          setWishlistOpen(false);
          setActiveProduct(item);
        }}
      />
    </section>
  );
}
